import { z } from 'zod'
import { VideoIdSchema } from '../video'
import { TiptapDocSchema } from '../tiptap'
import { UTCMillisecondsSchema } from '../time'
import { CompanyIdSchema, UserIdSchema } from '../ids'
import { AuditPropsSchema, MediaSchema, UPLOAD_URL_DESCRIPTION } from '../common'
import { ArchivableSchema } from '../archive'
import { GameIdSchema } from './shared'
import { GameSettingSchema } from './setting'
import { GameCriteriaSchema, TiebreakerSchema } from './criteria'
import { BracketRoundSchema } from './bracket'
import { GameAwardSchema } from './award'

export const SEEDING_ROUND = 0

export const GameTypeSchema = z.enum(['ladder', 'raffle', 'bracket'])
export type GameType = z.infer<typeof GameTypeSchema>

export const GameStatusSchema = z.enum(['draft', 'published', 'active', 'finished'])
export type GameStatus = z.infer<typeof GameStatusSchema>

export const GameTermsSchema = z.string().or(TiptapDocSchema)
export type GameTerms = z.infer<typeof GameTermsSchema>

const BaseGameSchema = z.object({
  gameId: GameIdSchema.optional(),
  timezone: z.string().optional(),
  name: z.string().nonempty(),
  startDate: UTCMillisecondsSchema,
  endDate: UTCMillisecondsSchema,
  announceDate: UTCMillisecondsSchema.optional().describe(
    'optional date by which to reveal/announce the competition to all competitors'
  ),
  status: GameStatusSchema,
  tagline: z.string().optional().describe('short catch phrase for the game'),
  description: z.string().optional(),
  color: z.string().optional(),
  awards: z.array(GameAwardSchema),
  criteria: z.array(GameCriteriaSchema),
  seedingCriteria: z
    .array(GameCriteriaSchema)
    .optional()
    .describe('criteria to use for seeding, only applicable for bracket games'),
  settings: GameSettingSchema,
  terms: GameTermsSchema.optional(),
})

export const LadderGameSchema = z.object({
  ...BaseGameSchema.shape,
  gameType: GameTypeSchema.extract(['ladder']),
  tiebreakers: TiebreakerSchema.optional(),
})
export type UnsavedLadderGameData = z.infer<typeof LadderGameSchema>
export type LadderGameData = Extract<GameData, { gameType: 'ladder' }>

export const RaffleGameSchema = z.object({
  ...BaseGameSchema.shape,
  gameType: GameTypeSchema.extract(['raffle']),
  winnersDrawn: z.boolean().optional().describe('Whether the winners have been drawn on the frontend'),
})
export type UnsavedRaffleGameData = z.infer<typeof RaffleGameSchema>
export type RaffleGameData = Extract<GameData, { gameType: 'raffle' }>

export const BracketGameSchema = z.object({
  ...BaseGameSchema.shape,
  gameType: GameTypeSchema.extract(['bracket']),
  rounds: z.array(BracketRoundSchema),
  tiebreakers: TiebreakerSchema.optional(),
})
export type UnsavedBracketGameData = z.infer<typeof BracketGameSchema>
export type BracketGameData = Extract<GameData, { gameType: 'bracket' }>

export const UnsavedGameSchema = z.discriminatedUnion('gameType', [
  LadderGameSchema,
  RaffleGameSchema,
  BracketGameSchema,
])
export type UnsavedGame = z.infer<typeof UnsavedGameSchema>

export const GameDataSchema = UnsavedGameSchema.and(
  z.object({
    gameId: GameIdSchema,
    companyId: CompanyIdSchema,
    ownerId: UserIdSchema,
    currentRound: z.number().gte(0).describe('The current round of the game'),
    cover: z
      .object({ ...MediaSchema.shape, upload: z.string().optional().meta({ description: UPLOAD_URL_DESCRIPTION }) })
      .optional(),
    video: z.object({ videoId: VideoIdSchema }).optional(),
    failReason: z
      .string()
      .optional()
      .describe('If the game failed a schedule worker, this field will contain the reason'),
  })
)
  .and(AuditPropsSchema)
  .and(ArchivableSchema)
export type GameData = z.infer<typeof GameDataSchema>

export type BracketOrLadderGame = BracketGameData | LadderGameData
